import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { buildLocationTabApiParams, LocationTabLocationQueryKey } from './location-tab-list.utils';
import { TicketListItemType } from '../../types/ticket/TicketListItem.type';
import { NewOrderType } from '../../types/order/order.type';
import { ServiceTicketType } from '../../types/service-calls/service-calls.type';

type LocationTabListRequest = RouteParamsType & {
  locationId: number;
  organizationId: number;
  controller?: AbortController;
};

type CreateLocationTabListThunkOptions = {
  typePrefix: string;
  urlPath: string;
  locationQueryKey: LocationTabLocationQueryKey;
  fallbackError: string;
};

export const createLocationTabListThunk = <T>({
  typePrefix,
  urlPath,
  locationQueryKey,
  fallbackError,
}: CreateLocationTabListThunkOptions) =>
  createAsyncThunk<PaginationType<T>, LocationTabListRequest, { state: RootReducerState }>(
    typePrefix,
    async (data, thunkAPI) => {
      const { controller, locationId, organizationId, ...params } = data;
      try {
        const response = await axios.get<PaginationType<T>>(`${process.env.REACT_APP_API_URL}${urlPath}`, {
          params: buildLocationTabApiParams(params, locationId, organizationId, locationQueryKey),
          signal: controller?.signal,
        });
        return response.data;
      } catch (error) {
        return rejectWithApiError({ thunkAPI, error, fallback: fallbackError });
      }
    }
  );

export const getLocationTicketsTabList = createLocationTabListThunk<TicketListItemType>({
  typePrefix: 'locations/getLocationTicketsTabList/no-loader',
  urlPath: '/ticket',
  locationQueryKey: 'location_id',
  fallbackError: 'Error during fetching tickets!',
});

export const getLocationOrdersTabList = createLocationTabListThunk<NewOrderType>({
  typePrefix: 'locations/getLocationOrdersTabList/no-loader',
  urlPath: '/inventory/order',
  locationQueryKey: 'location_id',
  fallbackError: 'Error during fetching orders!',
});

export const getLocationServiceCallsTabList = createLocationTabListThunk<ServiceTicketType>({
  typePrefix: 'locations/getLocationServiceCallsTabList/no-loader',
  urlPath: '/service-ticket',
  locationQueryKey: 'locationId',
  fallbackError: 'Error during fetching service calls!',
});
